/**
 * Global logout utility (Kit19 SSO ecosystem).
 *
 * Logout sequence:
 *  1. Publish an MQTT logout event so other Kit19 apps on this device sign out too.
 *  2. Clear this app's local session.
 *  3. Bounce through the SSO server's logout page to clear the central SSO
 *     session cookie, then return to /login. Without step 3, the central
 *     cookie stays alive and the user is silently auto-signed back in.
 *
 * ── PER-PROJECT CONFIG ──────────────────────────────────────────────────────
 *  • APP_NAME            : give each microservice a unique value.
 *  • clearLocalSession() : clear YOUR app's auth/session keys.
 *  • SSO logout URL      : set via RUNTIME_CONFIG.SSO_LOGOUT_URL / env / default.
 *    If you already have an SSO client with a logout(returnUrl) method, you can
 *    replace the inline redirect below with a call to it.
 * ────────────────────────────────────────────────────────────────────────────
 */

import { publishLogoutEvent, getDeviceId } from '../services/mqttLogoutService';

// TODO: set a unique identifier for this microservice (e.g. 'Kit19HR', 'Kit19Sales').
const APP_NAME = 'Kit19Sales';

// SSO logout URL: runtime config → env → default.
const resolveSsoLogoutUrl = () => {
  const rc = (typeof window !== 'undefined' && window.RUNTIME_CONFIG) || {};
  return rc.SSO_LOGOUT_URL || process.env.REACT_APP_SSO_LOGOUT_URL || 'https://sso.crmapp.in.net/logout';
};

// TODO: clear YOUR app's auth state here (token key, session keys, redux, etc.).
const clearLocalSession = () => {
  const deviceId = localStorage.getItem('kit19_device_id');
  const ssoConfig = localStorage.getItem('kit19_sso_config');
  localStorage.clear();
  if (deviceId) localStorage.setItem('kit19_device_id', deviceId);
  if (ssoConfig) localStorage.setItem('kit19_sso_config', ssoConfig);
  sessionStorage.clear();
};

export const handleLogout = async () => {
  const deviceId = getDeviceId();
  // TODO: adjust to wherever this app stores the logged-in user id.
  const userId =
    sessionStorage.getItem('userId') ||
    localStorage.getItem('kit_userId') ||
    localStorage.getItem('userId');

  // 1. Notify other Kit19 apps on this device. IMPORTANT: read userId BEFORE
  //    clearing the session (step 2), otherwise the publish is skipped.
  if (deviceId && userId) {
    try {
      await publishLogoutEvent(deviceId, userId, APP_NAME);
      await new Promise((resolve) => setTimeout(resolve, 500)); // let it propagate
    } catch {
      // Continue logging out even if MQTT publish fails.
    }
  }

  const authType = localStorage.getItem('kit_auth_type');
  
  // 2. Clear local session.
  clearLocalSession();

  if (authType === 'sso' || !authType) {
    // 3. Bounce through SSO logout (clears central cookie) then back to /login.
    const returnUrl = `${window.location.origin}/login`;
    window.location.href = `${resolveSsoLogoutUrl()}?redirect_uri=${encodeURIComponent(returnUrl)}`;
  } else {
    // Normal / Local login: Just redirect
    window.location.href = '/login';
  }
};

export default handleLogout;
